import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../state";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { Box, Button, Typography, Container } from "@mui/material";
import {
  StripeCardElement,
  StripeCardElementChangeEvent,
} from "@stripe/stripe-js";
import LockIcon from "@mui/icons-material/Lock";
import { styled } from "@mui/material/styles";
import PaymentService from "../../services/paymentService";
import LoginService from "../../services/loginService";
import UserService from "../../services/userService";
import { User } from "../../entities/User";
import PremiumBadgeIcon from "../../img/icons/vip.png";
import NotificationService from "../../components/Notification/notificationService";
import { COLLECTION_TAB } from "../MyProfile/MyProfile";
import { COLLECTION_SECTION } from "../MyProfile/Collection/Collection";
import { selectSubscriptionState } from "./subscriptionSlice";

const CustomButton = styled(Button)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "#3366FF",
  color: "#FFFFFF",
  position: "relative",
  padding: "6px 16px",
});

const IconWrapper = styled("div")({
  position: "absolute",
  right: "16px",
  display: "flex",
  alignItems: "center",
});

interface PayButtonProps {
  text: string;
  isDisabled: boolean;
}

const PayButton = ({ text, isDisabled }: PayButtonProps) => {
  return (
    <CustomButton
      variant="blue"
      type="submit"
      disabled={isDisabled}
      sx={{
        marginTop: "20px",
        borderRadius: 2,
        paddingTop: "15px !important",
        paddingBottom: "15px !important",
        "&:hover": {
          backgroundColor: "#254EDB",
        },
      }}
    >
      <span>{text}</span>
      <IconWrapper>
        <LockIcon />
      </IconWrapper>
    </CustomButton>
  );
};

export interface StripeOptions {
  clientSecret: string;
  currency: Array<string>;
}

const Checkout = () => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { processPayment } = PaymentService();
  const { getUserDetails } = UserService();
  const { storeUser } = LoginService();
  const { error: errorNotification, success: successNotification } =
    NotificationService();
  const loginData = useSelector((state: RootState) => state.login);
  const isMonthly = useSelector((state: RootState) => state.subscription);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      return;
    }

    setLoading(true);
    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement as StripeCardElement,
    });

    if (error) {
      setErrorMessage(error.message || "An error occurred");
      setLoading(false);
      if (error.message) errorNotification(error.message);
      return;
    }

    try {
      let response = await processPayment(paymentMethod?.id, isMonthly);
      setErrorMessage(null);
      if (!response) {
        throw new Error();
      }
      if (loginData.userId) {
        let loginDetails = await getUserDetails(loginData.userId);
        if (loginDetails) {
          let userData = new User(
            loginData.userId,
            loginData.access_token,
            loginData.userName,
            loginData.refresh_token,
            loginData.access_token_exp,
            loginData.refresh_token_exp,
            loginDetails.role,
            loginData.email,
            loginData.profileImage,
            loginData.headerImage,
            loginData.userData,
            loginData.collection,
            loginData.social_networks,
            undefined
          );
          await storeUser(userData);

          successNotification("Subscription successful");
          navigate(
            `/my-profile/${loginData.userId}/${COLLECTION_TAB.COLLECTION}/${COLLECTION_SECTION.OVERVIEW}`
          );
        }
      }
    } catch (error) {
      setErrorMessage("Failed to process the subscription");
      errorNotification("Something went wrong");
    }
    setLoading(false);
  };

  const handleCardChange = (event: StripeCardElementChangeEvent) => {
    if (event.error) {
      setErrorMessage(event.error.message || "Invalid card details");
    } else {
      setErrorMessage(null);
    }
  };

  useEffect(() => {
    if (!loginData.userId) {
      navigate("/");
    }
  }, []);

  return (
    <Container maxWidth={false} sx={{ padding: 0, backgroundColor: "#f2f8f8" }}>
      <Container maxWidth="lg" sx={{ paddingY: { xs: "0rem", sm: "3rem" } }}>
        <Box
          sx={{
            borderBottom: "2px solid #e3e0e0",
            paddingY: "1.5rem",
          }}
        >
          <Box>
            <Typography variant="upgradeTitle" textAlign="start">
              Review order
            </Typography>
          </Box>
          <Typography variant="upgradeSubtitle" textAlign="start">
            Choose the subscription that suits you
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            paddingY: "2rem",
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", sm: "calc(50% - 16px)" },
              backgroundColor: "#fff",
              padding: {
                xs: "1.5rem 1rem",
                sm: "2rem 1.5rem",
                md: "2rem 3rem",
              },
              borderRadius: "10px",
              marginBottom: { xs: "1rem", sm: "0rem" },
            }}
          >
            <Typography
              variant="upgradeTitle"
              textAlign="start"
              marginBottom="30px"
            >
              Card Details
            </Typography>
            <Box sx={{ marginTop: "15px" }}>
              <form onSubmit={handleSubmit} className="cardForm">
                <CardElement onChange={handleCardChange} />
                <PayButton
                  isDisabled={!stripe || loading}
                  text={
                    loading
                      ? "Processing..."
                      : isMonthly
                        ? "Pay €5,00"
                        : "Pay €50,00"
                  }
                />
                {errorMessage && <div>{errorMessage}</div>}
              </form>
            </Box>
          </Box>
          <Box
            sx={{
              width: { xs: "100%", sm: "calc(50% - 16px)" },
              height: "fit-content",
              backgroundColor: "#fff",
              padding: {
                xs: "1.5rem 1rem",
                sm: "2rem 1.5rem",
                md: "2rem 3rem",
              },
              borderRadius: "10px",
            }}
          >
            <Typography
              variant="featureText"
              sx={{
                fontWeight: "600 !important",
                marginBottom: "0.5rem !important",
                display: "block",
              }}
            >
              My Order
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ marginRight: "10px" }}>
                <img loading="lazy" width="20" src={PremiumBadgeIcon} alt="" />
              </Box>
              <Typography variant="upgradeTitle">
                VIP upgrade for a {isMonthly ? "month" : "year"}
              </Typography>
            </Box>
            <Typography
              variant="bannerDescription"
              sx={{ fontSize: "2rem !important", fontWeight: "700 !important" }}
            >
              {isMonthly ? "€5,00" : "€50,00"}
            </Typography>
          </Box>
        </Box>
      </Container>
    </Container>
  );
};

export default Checkout;
